/**
 * BomTab — container for the BOM tab on /manufacturing.
 *
 * Hosts a mode toggle (Explore / Simulate) above the actual tree. The two
 * modes used to be separate top-level tabs but they share ~80% of the
 * SVG/pan-zoom/expand-state logic, so the merge ships under one BOM tab
 * with the toggle picking which wrapper to render.
 *
 * URL state lives on the parent ManufacturingPage via `?mode=explore|
 * simulate`. The parent passes the current mode + a setter down so the
 * toggle stays the single mutation point.
 */
import { Box, Stack, ToggleButton, ToggleButtonGroup, Typography } from '@mui/material';
import {
  BomWithLines,
  ManufacturingProduct,
  OnshapeDocument,
  PartsByCodeResponse,
} from '../../types/manufacturing';
import { BomTreeExplore } from './BomTreeExplore';
import { BomTreeSimulate } from './BomTreeSimulate';

export type BomMode = 'explore' | 'simulate';

interface Props {
  mode: BomMode;
  onModeChange: (mode: BomMode) => void;
  boms: BomWithLines[];
  products: ManufacturingProduct[];
  onshapeDocuments?: OnshapeDocument[];
  // default_code -> Onshape part + drawing/PDF. Powers the (i) and PDF
  // chips on every node; falls back to no chip when unset.
  partsByCode?: PartsByCodeResponse;
  stockSyncedAtFallback?: string | null;
  loading: boolean;
}

export function BomTab({
  mode,
  onModeChange,
  boms,
  products,
  onshapeDocuments,
  partsByCode,
  stockSyncedAtFallback,
  loading,
}: Props) {
  return (
    <Box>
      <Stack direction="row" alignItems="center" spacing={2} sx={{ mb: 2 }}>
        <ToggleButtonGroup
          value={mode}
          exclusive
          size="small"
          color="primary"
          onChange={(_e, value: BomMode | null) => {
            // MUI emits null when the user clicks the already-selected
            // button. Ignore — exclusive groups should always have one
            // option active.
            if (value !== null) onModeChange(value);
          }}
          data-testid="manufacturing-mode-toggle"
        >
          <ToggleButton value="explore" data-testid="manufacturing-mode-explore">
            Explore
          </ToggleButton>
          <ToggleButton value="simulate" data-testid="manufacturing-mode-simulate">
            Simulate
          </ToggleButton>
        </ToggleButtonGroup>
        <Typography variant="caption" color="text.secondary">
          {mode === 'explore'
            ? 'Browse the full BOM hierarchy with Onshape + work-instruction links.'
            : 'Pick a manufactured product + quantity to check stock coverage.'}
        </Typography>
      </Stack>

      {mode === 'explore' ? (
        <BomTreeExplore
          boms={boms}
          products={products}
          onshapeDocuments={onshapeDocuments}
          partsByCode={partsByCode}
          loading={loading}
        />
      ) : (
        <BomTreeSimulate
          products={products}
          partsByCode={partsByCode}
          stockSyncedAtFallback={stockSyncedAtFallback}
        />
      )}
    </Box>
  );
}
